export default defineNuxtPlugin((nuxtApp) => {
  const log = useLogger('plugin:api-client')
  const sbAccessToken = useCookie('sb-access-token')

  let isAppMounted = false
  nuxtApp.hook('app:mounted', () => {
    isAppMounted = true
    log.info('[mount] App mounted, session modal enabled')
  })

  // Log Supabase auth state changes (token refresh, sign-out, etc.)
  nuxtApp.hook('app:mounted', async () => {
    const supabase = useSupabaseClient()
    supabase.auth.onAuthStateChange((event, session) => {
      log.info(`[auth] ${event}`, session ? `expires_at=${session.expires_at}` : 'no session')
    })
  })

  // Hook into Nuxt's global $fetch instance
  nuxtApp.hook('app:created', () => {
    const globalFetch = globalThis.$fetch
    const originalFetch = globalFetch

    globalThis.$fetch = async (request, options = {}) => {
      const requestUrl = request.toString()
      const isApiRoute = requestUrl.includes('/api/')

      // Auto-add Authorization header if token exists and it's an API call
      if (isApiRoute && sbAccessToken.value) {
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${sbAccessToken.value}`,
        }
      }

      try {
        return await originalFetch(request, options)
      }
      catch (error: unknown) {
        const fetchError = error as {
          response?: { status?: number }
          message?: string
          statusMessage?: string
          data?: { message?: string, statusMessage?: string, redirectTo?: string }
        }
        const status = fetchError.response?.status

        // Extract error message from various possible locations
        const errorMessage = fetchError.data?.message
          || fetchError.data?.statusMessage
          || fetchError.statusMessage
          || fetchError.message
          || ''

        // Only handle auth errors for API endpoints
        if (!isApiRoute) {
          throw error
        }

        const is401 = status === 401
        const is403BusinessError = status === 403 && errorMessage.includes('business ID')
        const is403PausedSubscription = status === 403 && errorMessage.includes('paused')

        // Don't intercept auth errors on auth pages (login, logout, etc.)
        const currentPath = window.location.pathname
        const isAuthPage = currentPath.startsWith('/auth/')
        if (isAuthPage) {
          log.info('🚫 On auth page, skipping session restoration')
          throw error
        }

        // Handle paused subscription - redirect to paused page
        if (is403PausedSubscription) {
          log.warn('⏸️ Business paused, redirecting to paused page')
          await navigateTo('/paused')
          throw error
        }

        // Handle 401 (token expired) or 403 (business mismatch)
        if (is401 || is403BusinessError) {
          const sessionRestoration = useSessionRestoration()

          // 1. For 401, try silent refresh first
          if (is401) {
            log.info('🔄 Attempting silent token refresh for 401...')
            const refreshed = await sessionRestoration.attemptSilentRefresh()

            if (refreshed) {
              // Retry the request with new token
              log.info('✅ Token refreshed, retrying request')
              const newToken = useCookie('sb-access-token')
              const retryOptions = {
                ...options,
                headers: {
                  ...(options.headers as Record<string, string> || {}),
                  Authorization: `Bearer ${newToken.value}`,
                },
              }
              return await originalFetch(request, retryOptions)
            }
          }

          // 2. Silent refresh failed or 403
          log.warn(`🔐 Session error (${status})`)

          // App not mounted yet — redirect to login to prevent WSOD
          if (!isAppMounted) {
            log.warn(`[auth] 401 before mount, redirecting to /auth/login (url=${requestUrl})`)
            window.location.replace('/auth/login')
            throw error
          }

          // If modal is already open, queue request instead of throwing
          if (sessionRestoration.isOpen()) {
            log.info('🔄 Modal already open, queuing request')
            return new Promise((resolve, reject) => {
              sessionRestoration.queueRequest({
                request: requestUrl,
                options: options as Record<string, unknown>,
                resolve: resolve as (value: unknown) => void,
                reject,
              })
            })
          }

          return new Promise((resolve, reject) => {
            // Queue this request for retry
            sessionRestoration.queueRequest({
              request: requestUrl,
              options,
              resolve,
              reject,
            })

            // Show modal and handle result
            sessionRestoration.showModal()
              .then(async (result) => {
                log.info('📋 Modal closed with result:', JSON.stringify(result))

                if (result.success) {
                  // Clear queued requests since URLs may contain stale business IDs
                  sessionRestoration.rejectAllRequests()
                  // Reload page to get fresh data with correct business context
                  log.info('✅ Session restored, reloading page')
                  window.location.reload()
                }
                else if (result.cancelled) {
                  // User cancelled - redirect to login
                  log.info('🚪 User cancelled, redirecting to login')
                  sessionRestoration.rejectAllRequests()
                  // Use setTimeout to ensure redirect happens after Promise chain settles
                  setTimeout(() => {
                    window.location.replace('/auth/login')
                  }, 0)
                }
                else {
                  // Modal closed without success - reject all
                  sessionRestoration.rejectAllRequests()
                }
              })
              .catch((modalError) => {
                // Modal failed to open - reject all queued requests
                log.error('❌ Failed to show session restore modal:', modalError)
                sessionRestoration.rejectAllRequests()
                reject(error) // Reject with original error
              })
          })
        }

        throw error
      }
    }

    // Copy over any existing properties
    Object.setPrototypeOf(globalThis.$fetch, originalFetch)
    Object.assign(globalThis.$fetch, originalFetch)
  })
})
